<app-entity-mat-dialog-title *ngIf="!inlineMode" [title]="'PAYMENTS.RECEIPT.TITLE' | translate"></app-entity-mat-dialog-title>

<!-- Dialog mode scrolls the body only, so the title bar stays put. -->
<mat-dialog-content *ngIf="!inlineMode">
  <ng-container *ngTemplateOutlet="receiptTpl"></ng-container>
</mat-dialog-content>

<ng-container *ngIf="inlineMode" [ngTemplateOutlet]="receiptTpl"></ng-container>

<ng-template #receiptTpl>
  <div class="receipt-container">
    <mat-progress-spinner *ngIf="loading" mode="indeterminate" diameter="40"></mat-progress-spinner>

    <!-- Debug section - only shown when we have issues or in development -->
    <div *ngIf="!loading && getPackageName() === 'N/A' && getStudentName() === 'N/A'" class="debug-info" style="display: none">
      <h4>Debug Information (Check Console)</h4>
      <pre>{{ paymentData | json }}</pre>
    </div>

    <div *ngIf="!loading" class="receipt-content">
      <div class="receipt-header">
        <div class="actions mt-3">
          <button style="background-color: transparent !important; border: none;"
            (click)="printReceipt()"
            type="button"
            mat-icon-button
            class="mb-1"
            [matTooltip]="'PAYMENTS.RECEIPT.PRINT_TOOLTIP' | translate"
            matTooltipPosition="below"
            aria-label="Print receipt">
            <mat-icon style="color: #1C3F8B;">print</mat-icon>
          </button>
          <button mat-raised-button color="accent" (click)="emailReceipt()" [matTooltip]="'PAYMENTS.RECEIPT.EMAIL_TOOLTIP' | translate">
            <mat-icon>email</mat-icon> {{ 'PAYMENTS.RECEIPT.EMAIL' | translate }}
          </button>
        </div>

        <h2 class="business-name">{{studioInfo.name || 'Business Name'}}</h2>
        <p class="business-address">{{studioInfo.address || 'Street Address'}}</p>
        <p class="business-city">{{studioInfo.city || 'City'}},
           {{studioInfo.state || 'State'}}
           {{studioInfo.zipCode || 'Zip Code'}}</p>
        <p class="business-phone">{{studioInfo.phone || 'Phone Number'}}</p>
      </div>

      <div class="receipt-body">
        <div class="receipt-info">
          <div class="info-row">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.PACKAGE_NAME' | translate }}</span>
            <span class="info-value">{{getPackageName()}}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.PAYMENT_ID' | translate }}</span>
            <span class="info-value">{{paymentData.field_payment_id || 'N/A'}}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.DATE' | translate }}</span>
            <span class="info-value">{{getFormattedDate(paymentData.field_date_and_time)}}</span>
          </div>
        </div>

        <div class="payment-details">
          <div class="info-row">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.PAYMENT_METHOD' | translate }}</span>
            <span class="info-value">{{getPaymentTypeName()}}</span>
          </div>
          <div class="info-row" *ngIf="getCardNumberDisplay()">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.CARD_NUMBER' | translate }}</span>
            <span class="info-value">{{getCardNumberDisplay()}}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.STUDENT' | translate }}</span>
            <span class="info-value">{{getStudentName()}}</span>
          </div>
          <div class="info-row" *ngIf="shouldShowFiscalCode() && getFiscalCode()">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.FISCAL_CODE' | translate }}</span>
            <span class="info-value">{{getFiscalCode()}}</span>
          </div>
          <div class="info-row">
            <span class="info-label">{{ (isRefundReceipt() ? 'PAYMENTS.RECEIPT.AMOUNT_REFUNDED' : 'PAYMENTS.RECEIPT.AMOUNT_PAID') | translate }}</span>
            <span class="info-value">{{ paymentData.field_gross_tuition | studioCurrency }}</span>
          </div>
          <div class="info-row" *ngIf="paymentData.field_tax_collected">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.TAX' | translate }}</span>
            <span class="info-value">{{ paymentData.field_tax_collected | studioCurrency }}</span>
          </div>
          <div class="info-row total">
            <span class="info-label">{{ 'PAYMENTS.RECEIPT.TOTAL' | translate }}</span>
            <span class="info-value">{{ (paymentData.field_total_payment_amount || paymentData.field_gross_tuition) | studioCurrency }}</span>
          </div>
          <!-- Gateway card surcharge: itemised separately (card-brand rules) and
               excluded from tuition/tax/total. -->
          <ng-container *ngIf="getSurcharge()">
            <div class="info-row">
              <span class="info-label">{{ 'PAYMENTS.RECEIPT.SURCHARGE' | translate }}</span>
              <span class="info-value">{{ getSurcharge() | studioCurrency }}</span>
            </div>
            <div class="info-row total">
              <span class="info-label">{{ 'PAYMENTS.RECEIPT.TOTAL_CHARGED' | translate }}</span>
              <span class="info-value">{{ getTotalCharged() | studioCurrency }}</span>
            </div>
          </ng-container>
          <!-- Cancellation fee withheld from a refund: itemised like the
               surcharge; the fee is not tuition or tax; the Total above is
               the net actually returned. -->
          <ng-container *ngIf="getCancellationFee()">
            <div class="info-row" *ngIf="isRefundWithFee()">
              <span class="info-label">{{ 'PAYMENTS.RECEIPT.REFUND_BEFORE_FEE' | translate }}</span>
              <span class="info-value">{{ getRefundBeforeFee() | studioCurrency }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ 'PAYMENTS.RECEIPT.CANCELLATION_FEE' | translate }}</span>
              <span class="info-value">{{ getCancellationFee() | studioCurrency }}</span>
            </div>
          </ng-container>
        </div>

        <div class="signature-section">
          <div class="signature-line">______________________________</div>
          <div class="signature-label">{{ 'PAYMENTS.RECEIPT.CARDHOLDER_SIGNATURE' | translate }}</div>
          <div class="cardholder-name">{{paymentData.field_customer_name || 'Cardholder Name'}}</div>
        </div>

        <div class="agreement-section">
          <h3>{{ 'PAYMENTS.RECEIPT.AGREEMENT_STATEMENTS' | translate }}</h3>
          <ul>
            <li>{{ 'PAYMENTS.RECEIPT.AGREEMENT_PAY' | translate }}</li>
            <li>{{ 'PAYMENTS.RECEIPT.AGREEMENT_AUTHORIZE' | translate:{ studioName: (studioInfo.name || 'Company Name'), enrollmentId: getEnrollmentId() } }}</li>
            <li *ngIf="paymentData.field_scheduled_payment">{{ 'PAYMENTS.RECEIPT.PAYMENTS_DUE' | translate }}</li>
            <li>{{ 'PAYMENTS.RECEIPT.AGREEMENT_CANCELLATION' | translate }}</li>
          </ul>
        </div>

        <div class="receipt-footer">
          <p>{{ 'PAYMENTS.RECEIPT.CARDHOLDER_COPY' | translate }}</p>
          <p>{{ 'PAYMENTS.RECEIPT.RETAIN_COPY' | translate }}</p>
          <p class="receipt-date">{{ 'PAYMENTS.RECEIPT.RECEIPT_GENERATED' | translate }} {{reportDateService.formatDateTime(today)}}</p>
        </div>
      </div>
    </div>
  </div>
</ng-template>
